import { Route } from '@angular/router';

import { ROUTE_NAMES } from '@constants';
import { CommunityProfileType, PermissionLevel, UserSubType } from '@models';
import type { AppState } from '@store';

export type NavigationItem = {
  link: string;
  route?: ROUTE_NAMES;
  tooltip: string;
  icon: string;
  iconOutline: string;
  count?: number;
  target?: string;
};

interface NavigationContext {
  userBean: AppState['user'];
  community: AppState['community'];
  root: AppState['root'];
}

export const buildNavigationItems = ({
  userBean,
  community,
  root,
}: NavigationContext): NavigationItem[] => {
  const items: NavigationItem[] = [];
  const isSigningRoomCommunity =
    community.communityProfileId === CommunityProfileType.SIGNING_ROOM;

  if (userBean.functions) {
    if (
      userBean.communityProfileId !== CommunityProfileType.PROJECT_ROOM &&
      userBean.communityProfileId !== undefined
    ) {
      items.push({
        link: '/home',
        route: ROUTE_NAMES.HOME,
        tooltip: 'WEB_HOME_AAA',
        icon: 'bi bi-house-fill',
        iconOutline: 'bi bi-house',
      });
    }

    if (!isSigningRoomCommunity) {
      if (root.rootRoomsPermission >= 1) {
        items.push({
          link: '/rooms',
          route: ROUTE_NAMES.ROOMS,
          tooltip: 'WEB_ROOM_LIST',
          icon: 'agoicon-room',
          iconOutline: 'agoicon-room_outline',
        });
      } else {
        items.push({
          link: '/shared-with-me',
          route: ROUTE_NAMES.ROOMS,
          tooltip: 'WEB_SHARED_WITH_ME',
          icon: 'agoicon-room',
          iconOutline: 'agoicon-room_outline',
        });
      }

      if (
        userBean.communitySignature &&
        (userBean.subtype === UserSubType.MEMBER ||
          userBean.subtype === UserSubType.AZURE_AD ||
          userBean.signature)
      ) {
        items.push({
          link: '/signingroom',
          route: ROUTE_NAMES.SIGNING_ROOM,
          tooltip: 'WEB_BILLING_PROFILE_3',
          icon: 'agoicon-sign font-size-28',
          iconOutline: 'agoicon-sign_outline font-size-28',
        });
      } else if (userBean.swisscomSign) {
        items.push({
          link: userBean.swisscomSign,
          target: '_blank',
          tooltip: 'Swisscom Sign',
          icon: 'agoicon-sign font-size-28',
          iconOutline: 'agoicon-sign_outline font-size-28',
        });
      }
    }

    if (userBean.subtype !== UserSubType.ONE_TIME_GUEST) {
      if (
        userBean.subtype !== UserSubType.GUEST &&
        (userBean.functions.events || userBean.functions.meetings)
      ) {
        items.push({
          link: '/calendar',
          route: ROUTE_NAMES.CALENDAR,
          tooltip: 'WEB_CALENDAR_AAA',
          icon: 'bi bi-calendar-fill',
          iconOutline: 'bi bi-calendar',
        });
      }

      if (userBean.functions.conversations) {
        items.push({
          link: '/messages',
          route: ROUTE_NAMES.MESSAGES,
          tooltip: 'WEB_CONVERSATIONS_AAA',
          icon: 'bi bi-chat-fill',
          iconOutline: 'bi bi-chat',
          count: 0,
        });
      }
    }

    if (!isSigningRoomCommunity) {
      items.push({
        link: '/recent',
        route: ROUTE_NAMES.RECENT,
        tooltip: 'WEB_HOME_RECENT',
        icon: 'bi bi-clock-history-fill',
        iconOutline: 'bi bi-clock-history',
        count: userBean.recentActivities ? userBean.recentActivities : 0,
      });
    }

    if (root.rootUsersPermission === PermissionLevel.MODIFY_AND_DELETE) {
      items.push({
        link: '/users',
        route: ROUTE_NAMES.USERS,
        tooltip: 'WEB_USER_LIST',
        icon: 'bi bi-person-fill',
        iconOutline: 'bi bi-person',
      });
    }

    if (root.rootGroupsPermission === PermissionLevel.MODIFY_AND_DELETE) {
      items.push({
        link: '/groups',
        route: ROUTE_NAMES.GROUPS,
        tooltip: 'WEB_GROUP_LIST',
        icon: 'bi bi-people-fill',
        iconOutline: 'bi bi-people',
      });
    }

    if (community.communityPermission >= 3) {
      if (community.communityReporting) {
        items.push({
          link: '/reporting',
          route: ROUTE_NAMES.REPORTING,
          tooltip: 'WEB_REPORTING_AAA',
          icon: 'bi bi-pie-chart-fill',
          iconOutline: 'bi bi-pie-chart',
        });
      }

      items.push({
        link: '/community',
        route: ROUTE_NAMES.COMMUNITY,
        tooltip: 'WEB_COMMUNITY_SETTINGS',
        icon: 'bi bi-gear-fill',
        iconOutline: 'bi bi-gear',
      });
    }
  }

  if (
    userBean.billing &&
    community.communityPermission === PermissionLevel.MODIFY_AND_DELETE
  ) {
    items.push({
      link: '/billing',
      route: ROUTE_NAMES.BILLING,
      tooltip: 'WEB_BILLING_AAA',
      icon: 'bi bi-credit-card-fill',
      iconOutline: 'bi bi-credit-card',
    });
  }

  return !userBean.showSigningRoom && !userBean.swisscomSign
    ? items.filter((item) => item.route !== ROUTE_NAMES.SIGNING_ROOM)
    : items;
};

const AREAS_REACHED_FROM: Partial<Record<ROUTE_NAMES, ROUTE_NAMES[]>> = {
  [ROUTE_NAMES.CALENDAR]: [ROUTE_NAMES.EVENTS],
};

export const canReachRoute = (items: NavigationItem[]) => {
  const links = new Set(items.map((item) => item.link));
  const linkedAreas = new Set(
    items.flatMap((item) => (item.route ? [item.route] : []))
  );
  const areas = new Set<ROUTE_NAMES>([
    ROUTE_NAMES.SEARCH,
    ...linkedAreas,
    ...[...linkedAreas].flatMap((area) => AREAS_REACHED_FROM[area] ?? []),
  ]);

  return (route: Route) => {
    const area = route.data?.['navigationRoute'] as ROUTE_NAMES | undefined;
    const isAreaPage =
      !!area && linkedAreas.has(area) && !route.path?.includes(':');

    return (
      !!area && areas.has(area) && (!isAreaPage || links.has(`/${route.path}`))
    );
  };
};
